import React, { useState, useEffect, useRef } from 'react';
import styles from './InitialTest.module.css';
import { useUser } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, apiRequest } from '../../config/api';
import Loader from '../common/Loader/Loader';

const InitialTest = () => {
  const navigate = useNavigate();
  const { userProgress, loading: userLoading, updateProgress } = useUser();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentCategory, setCurrentCategory] = useState('grammar');
  const initialized = useRef(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [completingTest, setCompletingTest] = useState(false);
  const [currentAnswer, setCurrentAnswer] = useState(null);
  const [answerSelected, setAnswerSelected] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(20); // Updated to 20 total questions
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);
  const questionsGenerated = useRef(0);
  const [questionDistribution] = useState({
    grammar: 6,
    writing: 6,
    spelling: 8
  });
  const categoryCount = useRef({
    grammar: 0,
    writing: 0,
    spelling: 0
  });

  useEffect(() => {
    const startSession = async () => {
      try {
        // Start a new session for the initial test
        const sessionData = await apiRequest('/sessions/start', {
          method: 'POST',
          body: JSON.stringify({
            mode: 'initial_test',
            difficulty: userProgress?.currentLevel || 1,
            questions: [] // Start with empty questions, we'll add them as we go
          })
        });

        setSessionId(sessionData._id);
        localStorage.setItem('currentSessionId', sessionData._id);
        
        // Start generating questions
        await generateNextQuestion();
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (!initialized.current && userProgress && !userLoading) {
      startSession();
      initialized.current = true;
    }
  }, [userProgress, userLoading]);

  const generateNextQuestion = async () => {
    setLoadingNextQuestion(true);
    try {
      // Use userProgress.currentLevel or default to 1 if not available
      const difficulty = userProgress?.currentLevel || 1;
      
      // Determine which category to generate next
      let categoryToGenerate = 'grammar'; // Default
      
      if (categoryCount.current.grammar < questionDistribution.grammar) {
        categoryToGenerate = 'grammar';
        categoryCount.current.grammar++;
      } else if (categoryCount.current.writing < questionDistribution.writing) {
        categoryToGenerate = 'writing';
        categoryCount.current.writing++;
      } else if (categoryCount.current.spelling < questionDistribution.spelling) {
        categoryToGenerate = 'spelling';
        categoryCount.current.spelling++;
      }
      
      // Generate question type based on category
      let questionType = 'error_identification'; // Default for grammar
      if (categoryToGenerate === 'writing') {
        questionType = 'short_sentences';
      } else if (categoryToGenerate === 'spelling') {
        questionType = 'word_completion';
      }
      
      // Generate the question
      const questionData = await apiRequest(`/questions/generateOne?type=${questionType}&difficulty=${difficulty}&category=${categoryToGenerate}`);
      
      // Add the new question to our questions array
      setQuestions(prevQuestions => [...prevQuestions, questionData]);
      questionsGenerated.current++;
      
      // If this is the first question, stop loading
      if (questionsGenerated.current === 1) {
        setLoading(false);
      }
      
      // Continue generating questions until we have all totalQuestions
      if (questionsGenerated.current < totalQuestions) {
        // Generate next question in the background
        setTimeout(() => generateNextQuestion(), 500);
      }
    } catch (err) {
      console.error("Error generating question:", err);
      // Don't set error here to avoid interrupting the test
      // Just continue with what we have
    } finally {
      setLoadingNextQuestion(false);
    }
  };

  // Reset input values when current question changes
  useEffect(() => {
    setInputValue('');
    setTextareaValue('');
    setCurrentAnswer(null);
    setAnswerSelected(false);
  }, [currentQuestion]);

  const handleAnswer = async () => {
    if (isSubmitting) return;
    
    // Get the answer based on question type
    let answer;
    const currentQ = questions[currentQuestion];
    
    if (currentQ.category === 'grammar') {
      answer = currentAnswer;
    } else if (currentQ.category === 'spelling') {
      answer = inputValue;
    } else if (currentQ.category === 'writing') {
      answer = textareaValue;
    }
    
    if (!answer) return; // Don't proceed if no answer is selected
    
    // Check if this is the last generated question and we're still generating more
    const isLastGeneratedQuestion = currentQuestion === questions.length - 1;
    const stillGeneratingQuestions = questionsGenerated.current < totalQuestions;
    
    if (isLastGeneratedQuestion && stillGeneratingQuestions && loadingNextQuestion) {
      // Don't allow submission yet - we're still generating the next question
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Submit the answer for current question using apiRequest
      await apiRequest(`/sessions/${sessionId}/individual-answer`, {
        method: 'POST',
        body: JSON.stringify({
          question: currentQ, // Send the complete question object
          answer,
          timeSpent: 0 // You might want to track time spent per question
        })
      });

      const newAnswers = { ...answers, [currentQuestion]: answer };
      setAnswers(newAnswers);

      // If this was the last question, end the session
      if (currentQuestion + 1 >= totalQuestions) {
        setCompletingTest(true); // Show loading overlay
        
        const results = await apiRequest(`/sessions/${sessionId}/end`, {
          method: 'POST'
        });

        localStorage.removeItem('currentSessionId');
        localStorage.removeItem('selectedApp'); // Remove selectedApp from localStorage
        updateProgress(results);
        window.location.href = '/';
      } else {
        // Move to next question
        setCurrentQuestion(currentQuestion + 1);
        
        // Reset states
        setSelectedAnswer(null);
        setSubmittingAnswer(false);
        setInputValue('');
        setTextareaValue('');
        setCurrentAnswer(null);
        setAnswerSelected(false);
      }
    } catch (err) {
      setError(err.message);
      setSubmittingAnswer(false);
      setCompletingTest(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to render the appropriate question UI based on question type
  const renderQuestionContent = (question) => {
    if (question.category === 'spelling') {
      return (
        <div className={styles.spellingQuestion}>
          <div className={styles.questionText}>
            {question.content.text}
          </div>
          <div className={styles.spellingHints}>
            <p><strong>Hints:</strong> {question.content.hints?.join(', ')}</p>
          </div>
          <div className={styles.answerInput}>
            <input 
              type="text" 
              placeholder="Type your answer here"
              className={styles.spellingInput}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                setAnswerSelected(e.target.value.trim() !== '');
              }}
            />
          </div>
        </div>
      );
    } else if (question.category === 'writing') {
      return (
        <div className={styles.writingQuestion}>
          <div className={styles.questionText}>
            <p>Rewrite the entire text below with all errors corrected:</p>
            <div className={styles.textToCorrect}>{question.content.text}</div>
          </div>
          <div className={styles.answerInput}>
            <textarea 
              placeholder="Type the complete corrected text here"
              className={styles.writingInput}
              rows={5}
              value={textareaValue}
              onChange={(e) => {
                setTextareaValue(e.target.value);
                setAnswerSelected(e.target.value.trim() !== '');
              }}
            />
          </div>
        </div>
      );
    } else {
      // Default for grammar questions (multiple choice)
      return (
        <div className={styles.grammarQuestion}>
          <div className={styles.questionText}>
            {question.content.text}
          </div>
          <div className={styles.optionsContainer}>
            {question.content.options.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentAnswer(option);
                  setAnswerSelected(true);
                }}
                className={`${styles.optionButton} ${
                  currentAnswer === option ? styles.selected : ''
                }`}
                disabled={isSubmitting}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader size="large" text="Preparing your assessment..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className={styles.loading}>
        <p>Generating your first question...</p>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  return (
    <div className={styles.container}>
      {completingTest && (
        <div className={styles.overlay}>
          <Loader size="large" text="Submitting test..." />
        </div>
      )}
      
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Initial Assessment</h2>
          <div className={styles.questionInfo}>
            <span className={styles.questionCount}>
              Question {currentQuestion + 1} of {totalQuestions}
            </span>
            <span className={styles.categoryBadge} data-category={currentQ.category}>
              {currentQ.category.charAt(0).toUpperCase() + currentQ.category.slice(1)}
            </span>
          </div>
        </div>
        
        <div className={styles.progressContainer}>
          <div 
            className={styles.progressBar} 
            style={{ width: `${progress}%` }}
          />
        </div>

        {renderQuestionContent(currentQ)}
        
        <div className={styles.navigationButtons}>
          {/* {loadingNextQuestion && (
            <div className={styles.loadingNextQuestion}>
              <Loader size="small" text="Preparing next question..." />
            </div>
          )} */}
          <button 
            onClick={handleAnswer}
            className={styles.submitButton}
            disabled={
              isSubmitting || 
              !answerSelected || 
              (currentQuestion === questions.length - 1 && 
               questionsGenerated.current < totalQuestions && 
               loadingNextQuestion)
            }
          >
            {currentQuestion + 1 === totalQuestions ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>
          Take your time to answer carefully.
        </p>
        <p className={styles.footerText}>
          This test will help us personalize your learning experience.
        </p>
      </div>
    </div>
  );
};

export default InitialTest;